import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { StyledAsyncSelect } from "./StyledAsyncSelect";
import { type ClassNamesConfig, MultiValue, SingleValue } from "react-select";
import { useTRPC } from "@/trpc";
import { PROFILE_SELECT_CLASS_NAMES } from "./profile-select-styles";

const SPECIALTY_CACHE_TIME_MS = 5 * 60 * 1000;
const SPECIALTY_FETCH_RETRIES = 2;

type SpecialtyOption = { label: string; value: string };

type ExtraProps = {
  placeholder?: string;
  disabled?: boolean;
  useParent?: boolean;
  onBlur?: () => void;
  appearance?: "default" | "profile";
  inputId?: string;
  classNames?: ClassNamesConfig<SpecialtyOption, boolean>;
};

type SpecialtiesSelectProps = {
  value?: string | string[];
  onChange?: (value: string | string[] | null) => void;
  isMulti: boolean;
} & ExtraProps;

export function SpecialtiesSelect({
  value,
  onChange,
  onBlur,
  placeholder = "Select specialty",
  disabled = false,
  useParent = false,
  isMulti = false,
  appearance = "default",
  inputId,
  classNames,
}: SpecialtiesSelectProps) {
  const trpc = useTRPC();
  const selectedCodes = useMemo(() => {
    if (Array.isArray(value)) {
      return value;
    }
    return value ? [value] : [];
  }, [value]);
  const {
    data: specialties,
    isLoading,
    isError,
    refetch,
  } = useQuery(
    trpc.healthcareProfessionalTypes.getMedicalSpecialties.queryOptions(
      undefined,
      {
        staleTime: SPECIALTY_CACHE_TIME_MS,
        retry: SPECIALTY_FETCH_RETRIES,
      },
    ),
  );
  const selectedSpecialties = useQuery(
    trpc.healthcareProfessionalTypes.getByCodes.queryOptions(
      { codes: selectedCodes },
      { enabled: selectedCodes.length > 0, staleTime: SPECIALTY_CACHE_TIME_MS },
    ),
  );

  const specialtyOptions = useMemo(() => {
    if (!specialties) {
      return [];
    }
    return specialties
      .filter((specialty) => (useParent ? specialty.isParent : true))
      .map((specialty) => ({
        label: specialty.name,
        value: specialty.code,
      }));
  }, [specialties, useParent]);

  const selectedOption = useMemo(() => {
    // Imported taxonomy codes may be absent from the selectable catalog. Keep
    // them selected, resolving their labels independently, so opening or editing
    // this control never silently removes a provider's existing specialty.
    const labels = new Map(
      specialtyOptions.map((option) => [option.value, option.label]),
    );
    for (const specialty of selectedSpecialties.data ?? []) {
      labels.set(specialty.code, specialty.name);
    }
    const options = selectedCodes.map((code) => ({
      label: labels.get(code) ?? code,
      value: code,
    }));
    if (isMulti) {
      return options;
    }
    return options[0] ?? null;
  }, [selectedCodes, specialtyOptions, selectedSpecialties.data, isMulti]);

  const loadSpecialties = async (inputValue: string) => {
    let options: SpecialtyOption[] = specialtyOptions;
    if (!specialties) {
      const loaded = await refetch();
      options = (loaded.data ?? [])
        .filter((specialty) => !useParent || specialty.isParent)
        .map((specialty) => ({ label: specialty.name, value: specialty.code }));
    }
    const lowerInput = inputValue.toLowerCase();
    return options
      .map((option) => ({
        ...option,
        score: option.label.toLowerCase().indexOf(lowerInput),
      }))
      .filter((option) => option.score >= 0)
      .sort((a, b) => {
        // Exact matches first
        if (a.score === 0 && b.score !== 0) {
          return -1;
        }
        if (b.score === 0 && a.score !== 0) {
          return 1;
        }
        // Then by position of match
        return a.score - b.score;
      })
      .map(({ label, value }) => ({ label, value }));
  };

  const handleChange = (
    option:
      | MultiValue<{ label: string; value: string }>
      | SingleValue<{ label: string; value: string }>
      | null,
  ) => {
    if (isMulti) {
      const selectedOption = option as MultiValue<{
        label: string;
        value: string;
      }>;
      onChange?.(selectedOption ? selectedOption.map((o) => o.value) : []);
    } else {
      const selectedOption = option as {
        label: string;
        value: string;
      };
      onChange?.(selectedOption ? selectedOption.value : null);
    }
  };

  return (
    <div>
      <StyledAsyncSelect
        inputId={inputId}
        value={selectedOption}
        loadOptions={loadSpecialties}
        onChange={handleChange}
        options={specialtyOptions}
        defaultOptions={appearance === "profile" ? specialtyOptions : undefined}
        isLoading={isLoading || selectedSpecialties.isLoading}
        classNames={{
          ...(appearance === "profile"
            ? PROFILE_SELECT_CLASS_NAMES
            : undefined),
          ...classNames,
        }}
        menuPlacement={appearance === "profile" ? "auto" : undefined}
        isDisabled={disabled}
        placeholder={placeholder}
        isMulti={isMulti}
        onBlur={onBlur}
        noOptionsMessage={({ inputValue }) => {
          if (isLoading) {
            return "Loading specialties…";
          }
          if (isError) {
            return "Specialties are unavailable";
          }
          return inputValue
            ? "No specialties found"
            : "Type to search for specialties";
        }}
        isClearable
      />
      {isError && (
        <p role="alert" className="mt-2 text-xs text-red-700">
          Could not load specialties.{" "}
          <button
            type="button"
            className="font-semibold underline underline-offset-2"
            onClick={() => void refetch()}
          >
            Try again
          </button>
        </p>
      )}
    </div>
  );
}
